import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@src/reducers/store";
import {
  addDashboard,
  addDashboardAfter,
  updateDashboardMeta,
  updateDashboardPresentation,
  deleteDashboard,
  setSelectedDashboard,
  setDefaultDashboard,
} from "@src/reducers/dashboards-slice";
import {
  Dashboard,
  DashboardPresentation,
} from "@src/types/dashboards";
import { postCreateDashboardV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/post-create-dashboard-v2-use-case";
import { patchDashboardV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/patch-dashboard-v2-use-case";
import { deleteDashboardV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/delete-dashboard-v2-use-case";
import { postDuplicateDashboardV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/post-duplicate-dashboard-v2-use-case";
import {
  errorSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

// Per-dashboard request sequence counter for the colors PATCH. Each new
// optimistic dispatch stamps `sequence + 1` on the map; the request whose
// `sequence` no longer matches at PATCH resolution is considered stale and
// discards its result (no revert, no snackbar — a newer request has
// superseded it and owns the rollback decision). Race-free under rapid edits
// without needing an AbortController.
const paletteRequestSequence = new Map<string, number>();

export const useDashboardActions = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Returns the created dashboard so the caller can select it by its real id.
  const handleAddDashboard = useCallback(
    async (
      name: string,
      description: string | null = null,
    ): Promise<Dashboard | null> => {
      const result = await postCreateDashboardV2UseCase({
        name,
        description,
        status: "draft",
        is_default: false,
      });
      if (result.success && result.data) {
        dispatch(addDashboard(result.data));
        return result.data;
      }
      console.error("Failed to create dashboard:", result.error);
      return null;
    },
    [dispatch],
  );

  const handleUpdateDashboard = useCallback(
    async (id: string, name: string, description: string | null = null) => {
      const result = await patchDashboardV2UseCase(id, { name, description });
      if (result.success) {
        // Update only the renamed fields. `upsertDashboard` would replace
        // the entire entry and trigger every widget's data hook downstream
        // — see dashboards-ticket-list §patch-react-rerender.
        dispatch(updateDashboardMeta({ id, name, description }));
      } else {
        console.error("Failed to rename dashboard:", result.error);
      }
    },
    [dispatch],
  );

  const handleDeleteDashboard = useCallback(
    async (id: string) => {
      const result = await deleteDashboardV2UseCase(id);
      if (result.success) {
        dispatch(deleteDashboard(id));
      } else {
        console.error("Failed to delete dashboard:", result.error);
      }
    },
    [dispatch],
  );

  const handleSelectDashboard = useCallback(
    (id: string | null) => {
      dispatch(setSelectedDashboard(id));
    },
    [dispatch],
  );

  const handleSetDefaultDashboard = useCallback(
    async (id: string) => {
      const result = await patchDashboardV2UseCase(id, { is_default: true });
      if (result.success) {
        // `setDefaultDashboard(id)` already iterates every dashboard in
        // `state.dashboards` and rewrites `is_default` to (d.id === id),
        // so an additional `updateDashboardMeta({ is_default: true })`
        // would just be a redundant one-line overwrite.
        dispatch(setDefaultDashboard(id));
      } else {
        console.error("Failed to set default dashboard:", result.error);
      }
    },
    [dispatch],
  );

  const handleDuplicateDashboard = useCallback(
    async (id: string) => {
      const result = await postDuplicateDashboardV2UseCase(id);
      if (result.success && result.data) {
        // Copy goes below the original (doc §2.5.1)
        dispatch(addDashboardAfter({ afterId: id, dashboard: result.data }));
      } else {
        console.error("Failed to duplicate dashboard:", result.error);
      }
    },
    [dispatch],
  );

  const handlePublishDashboard = useCallback(
    async (id: string) => {
      const result = await patchDashboardV2UseCase(id, {
        status: "published",
      });
      if (result.success) {
        dispatch(updateDashboardMeta({ id, status: "published" }));
      } else {
        console.error("Failed to publish dashboard:", result.error);
      }
    },
    [dispatch],
  );

  // Optimistic colors update with revert on PATCH failure.
  // Dumb by design: takes the *full* previous presentation so it can
  // restore exactly (including future groups we don't touch). Caller
  // (editor hook) holds the snapshot. Returns `success: boolean` so
  // the caller can decide whether to surface a second snackbar /
  // chain follow-on actions.
  //
  // Race-safety: a per-dashboard sequence counter (`paletteRequestSequence`
  // above) lets us discard stale resolutions so a slow-failing PATCH-A
  // doesn't revert a newer edit B that the user applied during A's
  // round-trip. Only the latest in-flight request drives revert/snackbar.
  const handleUpdateDashboardColors = useCallback(
    async (
      id: string,
      nextPresentation: DashboardPresentation,
      previousPresentation: DashboardPresentation | null,
    ): Promise<{ success: boolean; error?: string }> => {
      const sequence = (paletteRequestSequence.get(id) ?? 0) + 1;
      paletteRequestSequence.set(id, sequence);

      // Optimistic dispatch — applies immediately, so the picker UI
      // re-tints before the BE round-trip.
      dispatch(
        updateDashboardPresentation({
          dashboardId: id,
          presentation: nextPresentation,
        }),
      );

      const result = await patchDashboardV2UseCase(id, {
        presentation: nextPresentation,
      });

      // If a newer request superseded ours while we awaited, leave the
      // current state alone. The newer request owns revert decision and
      // state application; touching now would clobber a fresher edit
      // that the user is still actively editing.
      if (paletteRequestSequence.get(id) !== sequence) {
        return { success: false, error: result.error };
      }

      if (!result.success) {
        // Revert: restore the snapshot exactly. Pass `null` if there was
        // no previous presentation so the reducer wipes the field.
        dispatch(
          updateDashboardPresentation({
            dashboardId: id,
            presentation: previousPresentation,
          }),
        );
        errorSnackbar(result.error ?? "Failed to update palette");
        return { success: false, error: result.error };
      }

      // Success: sync the BE echo so any server-side normalization
      // (stripped null keys, deep merges) lands in FE state. Targeted:
      // dispatching `upsertDashboard` here would replace the WHOLE
      // dashboard object and clobber locally-tracked state (pending
      // widgets the BE has never seen, ordering on the in-flight grid
      // drag, etc.), so we sync only the presentation field. Side
      // effects (renames, status flips, widget add/remove) are owned
      // by their respective action hooks and arrive via separate PATCHes
      // / GETs.
      if (result.data) {
        dispatch(
          updateDashboardPresentation({
            dashboardId: id,
            presentation: result.data.presentation ?? null,
          }),
        );
      }
      return { success: true };
    },
    [dispatch],
  );

  return {
    addDashboard: handleAddDashboard,
    updateDashboard: handleUpdateDashboard,
    deleteDashboard: handleDeleteDashboard,
    selectDashboard: handleSelectDashboard,
    setDefaultDashboard: handleSetDefaultDashboard,
    duplicateDashboard: handleDuplicateDashboard,
    publishDashboard: handlePublishDashboard,
    updateDashboardColors: handleUpdateDashboardColors,
  };
};
